import {
  CALLABLE_NAMES,
  OwnerBookingRefusal,
  paths,
  type CreateManualBookingRequest,
  type OwnerBookingResponse,
  type RescheduleBookingRequest,
} from '@acs/shared';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { appointmentConverter, type AppointmentDoc } from './converters.js';
import { db, functions } from './firebase.js';

/** Live subscription to upcoming confirmed appointments (dashboard). */
export function subscribeUpcoming(
  businessId: string,
  cb: (rows: AppointmentDoc[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, paths.appointments(businessId)).withConverter(appointmentConverter),
    where('status', '==', 'confirmed'),
    where('start', '>=', Timestamp.fromDate(new Date())),
    orderBy('start', 'asc'),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())), onError);
}

/** Live subscription to confirmed appointments in a start-time range (dashboard
 * week calendar). Same composite index as the upcoming query (status + start). */
export function subscribeAppointmentsInRange(
  businessId: string,
  from: Date,
  to: Date,
  cb: (rows: AppointmentDoc[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, paths.appointments(businessId)).withConverter(appointmentConverter),
    where('status', '==', 'confirmed'),
    where('start', '>=', Timestamp.fromDate(from)),
    where('start', '<', Timestamp.fromDate(to)),
    orderBy('start', 'asc'),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())), onError);
}

/** How far back a booking can start and still be running — the longest a
 * booking can be (a whole day). */
const LONGEST_BOOKING_MS = 24 * 60 * 60 * 1000;

/**
 * Confirmed bookings of one service that have not ENDED yet — upcoming or in
 * progress — which is what stops the service from being removed (doc 45). Read
 * through the (status, start) index the dashboard uses, from a day back so an
 * all-day booking under way is included, and filtered here: a business's book
 * from yesterday on is small, and a (serviceId, status, start) index would
 * exist for this one check.
 */
export async function listUpcomingForService(businessId: string, serviceId: string): Promise<AppointmentDoc[]> {
  const now = Date.now();
  const q = query(
    collection(db, paths.appointments(businessId)).withConverter(appointmentConverter),
    where('status', '==', 'confirmed'),
    where('start', '>=', Timestamp.fromMillis(now - LONGEST_BOOKING_MS)),
    orderBy('start', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data()).filter((a) => a.serviceId === serviceId && a.end.getTime() > now);
}

/** Owner cancellation — the only appointment mutation allowed client-side (rules). */
export async function cancelAppointment(businessId: string, appointmentId: string): Promise<void> {
  await updateDoc(doc(db, paths.appointment(businessId, appointmentId)), {
    status: 'cancelled',
    cancelledAt: serverTimestamp(),
  });
}

const createManualBookingFn = httpsCallable<CreateManualBookingRequest, OwnerBookingResponse>(
  functions,
  CALLABLE_NAMES.createManualBooking,
);
const rescheduleBookingFn = httpsCallable<RescheduleBookingRequest, OwnerBookingResponse>(
  functions,
  CALLABLE_NAMES.rescheduleBooking,
);

/** The owner books a customer by hand (doc 43). Server-side: creation is
 * server-only, the overlap check and client upsert run in one transaction. */
export async function createManualBooking(req: CreateManualBookingRequest): Promise<OwnerBookingResponse> {
  return (await createManualBookingFn(req)).data;
}

/** The owner moves a booking to a new date/time (doc 43 D2). The booking gets
 * a NEW id — the original is cancelled with `rescheduledToId`. */
export async function rescheduleBooking(req: RescheduleBookingRequest): Promise<OwnerBookingResponse> {
  return (await rescheduleBookingFn(req)).data;
}

/** Why the server refused an owner booking, when it was a decision rather than
 * a fault (`failed-precondition` with `details.reason`). */
export function ownerBookingRefusal(error: unknown): OwnerBookingRefusal | null {
  const e = error as { code?: string; details?: { reason?: unknown } } | null;
  if (e?.code !== 'functions/failed-precondition') return null;
  const parsed = OwnerBookingRefusal.safeParse(e.details?.reason);
  return parsed.success ? parsed.data : null;
}
